import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthYearPicker } from "@/components/ui/month-year-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api/_client";
import type { WithdrawalFormData, WithdrawalItem } from "@/lib/api/flow-instances/withdrawals";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./wealth-plan-dialog.i18n";

type WealthPlanDialogProps = {
  open: boolean;
  withdrawal: WithdrawalItem | null;
  defaultPeriodYears?: number;
  onClose: () => void;
  onSave: (data: WithdrawalFormData) => Promise<{ success: boolean; error?: string }>;
};

export function WealthPlanDialog({
  open,
  withdrawal,
  defaultPeriodYears = 10,
  onClose,
  onSave,
}: WealthPlanDialogProps) {
  const t = useT(T);
  const [formData, setFormData] = useState<WithdrawalFormData>({
    name: "",
    scheduleType: "one_time",
    oneTimeAmount: null,
    oneTimeDate: null,
    recurringAmount: null,
    recurringFrequency: "monthly",
    recurringStartDate: null,
    recurringEndDate: null,
  });
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  // Helper to format date as YYYY-MM for month input
  const formatYearMonth = (date: Date | string | null) => {
    if (!date) return null;
    // Stored values are "YYYY-MM-DD HH:MM:SS" (UTC): take the month as written,
    // never through the local timezone.
    return typeof date === "string" ? date.slice(0, 7) : date.toISOString().slice(0, 7);
  };

  // Helper to convert YYYY-MM to YYYY-MM-01 for storage
  const toFirstOfMonth = (yearMonth: string | null | undefined) =>
    yearMonth ? `${yearMonth}-01` : null;

  // Reset form when dialog opens/closes or when editing different withdrawal
  useEffect(() => {
    if (open) {
      if (withdrawal) {
        setFormData({
          name: withdrawal.name,
          scheduleType: withdrawal.scheduleType,
          oneTimeAmount: withdrawal.oneTimeAmount,
          oneTimeDate: formatYearMonth(withdrawal.oneTimeDate),
          recurringAmount: withdrawal.recurringAmount,
          recurringFrequency: withdrawal.recurringFrequency || "monthly",
          recurringStartDate: formatYearMonth(withdrawal.recurringStartDate),
          recurringEndDate: formatYearMonth(withdrawal.recurringEndDate),
        });
      } else {
        setFormData({
          name: "",
          scheduleType: "one_time",
          oneTimeAmount: null,
          oneTimeDate: null,
          recurringAmount: null,
          recurringFrequency: "monthly",
          recurringStartDate: null,
          recurringEndDate: null,
        });
      }
      setError(null);
    }
  }, [open, withdrawal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError(t.nameRequired);
      return;
    }

    if (formData.scheduleType === "one_time") {
      if (!formData.oneTimeAmount || formData.oneTimeAmount <= 0) {
        setError(t.amountPositive);
        return;
      }
      if (!formData.oneTimeDate) {
        setError(t.monthRequired);
        return;
      }
    } else {
      if (!formData.recurringAmount || formData.recurringAmount <= 0) {
        setError(t.amountPositive);
        return;
      }
      if (!formData.recurringStartDate) {
        setError(t.startMonthRequired);
        return;
      }
      if (formData.recurringEndDate && formData.recurringEndDate < formData.recurringStartDate) {
        setError(t.endBeforeStart);
        return;
      }
    }

    startTransition(async () => {
      // Convert YYYY-MM to YYYY-MM-01 for database storage
      const dataToSave: WithdrawalFormData = {
        ...formData,
        oneTimeDate: toFirstOfMonth(formData.oneTimeDate),
        recurringStartDate: toFirstOfMonth(formData.recurringStartDate),
        recurringEndDate: toFirstOfMonth(formData.recurringEndDate),
      };
      try {
        const result = await onSave(dataToSave);
        if (!result.success) {
          setError(result.error || t.failedToSave);
        } else {
          onClose();
        }
      } catch (err) {
        // The server's validation message (e.g. a malformed date) reaches the user.
        setError(apiErrorMessage(err, t.failedToSave));
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{withdrawal ? t.editWithdrawal : t.addWithdrawal}</DialogTitle>
          <DialogDescription>{withdrawal ? t.editDescription : t.addDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                {t.purpose} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.purposePlaceholder}
                required
              />
            </div>

            {/* Schedule Type */}
            <div className="space-y-2">
              <Label htmlFor="scheduleType">{t.type}</Label>
              <Select
                value={formData.scheduleType}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    scheduleType: value as "one_time" | "recurring",
                  })
                }
              >
                <SelectTrigger id="scheduleType">
                  <SelectValue placeholder={t.selectType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">{t.oneTime}</SelectItem>
                  <SelectItem value="recurring">{t.recurring}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* One-time fields */}
            {formData.scheduleType === "one_time" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="oneTimeAmount">
                    {t.amount} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="oneTimeAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.oneTimeAmount ?? ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        oneTimeAmount: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    placeholder="500000"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="oneTimeDate">
                    {t.month} <span className="text-destructive">*</span>
                  </Label>
                  <MonthYearPicker
                    value={formData.oneTimeDate}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        oneTimeDate: value,
                      })
                    }
                    placeholder={t.selectMonth}
                    required
                  />
                </div>
              </>
            )}

            {/* Recurring fields */}
            {formData.scheduleType === "recurring" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="recurringAmount">
                    {t.amount} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="recurringAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.recurringAmount ?? ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recurringAmount: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    placeholder="10000"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recurringFrequency">
                    {t.frequency} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.recurringFrequency || "monthly"}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        recurringFrequency: value as "monthly" | "quarterly" | "yearly",
                      })
                    }
                  >
                    <SelectTrigger id="recurringFrequency">
                      <SelectValue placeholder={t.selectFrequency} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">{t.monthly}</SelectItem>
                      <SelectItem value="quarterly">{t.quarterly}</SelectItem>
                      <SelectItem value="yearly">{t.yearly}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recurringStartDate">
                    {t.startMonth} <span className="text-destructive">*</span>
                  </Label>
                  <MonthYearPicker
                    value={formData.recurringStartDate}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        recurringStartDate: value,
                      })
                    }
                    placeholder={t.selectStartMonth}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recurringEndDate">{t.endMonthOptional}</Label>
                  <MonthYearPicker
                    value={formData.recurringEndDate}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        recurringEndDate: value,
                      })
                    }
                    placeholder={t.selectEndMonth}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t.endMonthNote(defaultPeriodYears)}
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {withdrawal ? t.save : t.addWithdrawal}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
